import { flowApi } from '.';
import { TAG_TYPES } from 'types/services';
import type {
  CreateRowReq,
  CreateRowRes,
  FilterColumnValue,
  GetAllRowsReq,
  GetAllRowsRes,
  GetRowRes,
  MappedFilterValue,
  ThreeLevelsFilterValue,
  UpdateRowReq,
  UpdateRowRes,
  ViewFilteringType,
} from 'types';

export const rowsService = flowApi.injectEndpoints({
  overrideExisting: true,

  endpoints: (builder) => ({
    getAllRows: builder.query<GetAllRowsRes, GetAllRowsReq>({
      query: ({ tableId, search, orderBy, orderDesc, page, pageSize, value }) => ({
        url: `Tables/${tableId}/Rows`,
        method: 'GET',
        params: {
          Search: search,
          OrderBy: orderBy,
          OrderDesc: orderDesc,
          Page: page,
          PageSize: pageSize,
          Value: value,
        },
      }),
      providesTags: [
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
      ],
    }),
    getAllViewRows: builder.query<
      GetAllRowsRes,
      {
        params: GetAllRowsReq;
        body: {
          valueColumns: Record<string, string | string[] | null | undefined>[];
          rowIds: string[];
          filters?: FilterColumnValue[];
          viewFilteringType?: ViewFilteringType;
        };
      }
    >({
      query: ({ params, body }) => ({
        url: `Tables/${params.tableId}/View/${params.viewId}/Rows`,
        method: 'POST',
        params: {
          Search: params.search,
          OrderBy: params.orderBy,
          OrderDesc: params.orderDesc,
          Page: params.page,
          PageSize: params.pageSize,
          Value: params.value,
        },
        body,
      }),
      providesTags: [
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
      ],
    }),
    getAllViewPrefilteredRows: builder.query<
      GetAllRowsRes['items'],
      {
        params: GetAllRowsReq;
        body: {
          rowPrefilterRequest: {
            agendaId: string;
            viewId: string;
            formItemId: string;
            recordsRequest: {
              recordIds: string[];
              valueFormItems: Record<string, string | string[] | null | undefined>[];
              params: Record<string, number | null>;
              filters: MappedFilterValue[] | ThreeLevelsFilterValue[];
              skipViewConditions: boolean;
              evaluateVisualAttributes: boolean;
            };
          };
          filterRequest: {
            tableId: string;
            viewId: string;
            valueColumns: Record<string, string | string[] | null | undefined>[];
            filters: FilterColumnValue[];
          };
          viewFilteringType?: ViewFilteringType;
        };
      }
    >({
      query: ({ params, body }) => ({
        url: `Tables/${params.tableId}/View/${params.viewId}/Rows/Prefiltered`,
        method: 'POST',
        params: {
          Search: params.search,
          OrderBy: params.orderBy,
          OrderDesc: params.orderDesc,
          Page: params.page,
          PageSize: params.pageSize,
          Value: params.value,
        },
        body,
      }),
      providesTags: [
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
      ],
    }),
    createRow: builder.mutation<CreateRowRes, CreateRowReq>({
      query: ({ tableId, rowValues }) => ({
        url: `Tables/${tableId}/Rows`,
        method: 'POST',
        body: {
          cells: rowValues,
        },
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.TABLE,
          id: 'LIST',
        },
      ],
    }),
    getRow: builder.query<GetRowRes, GetRowRes>({
      query: ({ tableName, rowId }) => ({
        url: `Tables/${tableName}/Rows/${rowId}`,
        method: 'GET',
      }),
      providesTags: [
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
      ],
    }),
    deleteRow: builder.mutation<
      void,
      {
        tableName: string;
        rowId: string;
      }
    >({
      query: ({ tableName, rowId }) => ({
        url: `Tables/${tableName}/Rows/${rowId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
      ],
    }),
    updateRow: builder.mutation<UpdateRowRes, UpdateRowReq>({
      query: ({ tableId, rowId, rowValues }) => ({
        url: `Tables/${tableId}/Rows/${rowId}`,
        method: 'PUT',
        body: {
          cells: rowValues,
        },
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.TABLE,
          id: 'LIST',
        },
      ],
    }),
    deleteRows: builder.mutation<
      void,
      {
        tableId: string;
        rowIds: string[];
      }
    >({
      query: ({ tableId, rowIds }) => ({
        url: `Tables/${tableId}/Rows`,
        method: 'DELETE',
        body: {
          rowIds,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [
        {
          type: TAG_TYPES.TABLE_DETAIL,
          id: 'LIST',
        },
        {
          type: TAG_TYPES.TABLE,
          id: 'LIST',
        },
      ],
    }),
  }),
});
export const {
  useGetAllRowsQuery,
  useLazyGetAllRowsQuery,
  useGetAllViewRowsQuery,
  useGetAllViewPrefilteredRowsQuery,
  useLazyGetAllViewRowsQuery,
  useCreateRowMutation,
  useDeleteRowMutation,
  useDeleteRowsMutation,
  useGetRowQuery,
  useLazyGetRowQuery,
  useUpdateRowMutation,
} = rowsService;
